import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { isAxiosError } from 'axios'

import * as styles from './ReservationDetailFrame.module.scss'

import { ReservationReceiptResponse } from '../../apis/fvillage'
import {
  ButtonWrapper,
  CancelPolicyDetailList,
  ContactDetailList,
  GuestDetailList,
  Loader,
  MainColumn,
  MultiColumn,
  NoteDetailList,
  ReceiptForm,
  ReservationDetailList,
  ReservationLayout,
  ReservationPanel,
  SideColumn,
  UsageInfoDetailList,
} from '../../components'
import {
  useAuthProvider,
  useCreateNextPath,
  useDownloadReceiptApi,
  useIssueReceiptApi,
  useLogin,
  usePriceApi,
  usePublication,
  useReceiptAvailability,
  useReservationApi,
  useReservationPath,
} from '../../hooks'
import { isReceiptAvailableJst, offersGuestReceipt } from '../../utils'

const translateNamespace = `frames.ReservationDetailFrame`

export const ReservationDetailFrame = () => {
  const { t, i18n } = useTranslation()
  const { name, token } = useAuthProvider()
  const navigate = useNavigate()
  const { onValidateTokenExpired } = useLogin()
  const { params } = useCreateNextPath()
  const reservationId = params.reservation
  const { reservation, error, isLoading, mutate } = useReservationApi({
    id: reservationId ?? '',
    token,
  })
  const { isReceiptAvailable } = useReceiptAvailability(
    reservation?.transaction?.input?.checkOutDate
  )
  const reservationInput = reservation?.transaction?.input
  // Same request (SWR key) as ReservationPanel's, so it adds no extra call.
  // Invoice-billed / post-paid bookings get no guest receipt (SB-3056).
  const { data: priceData, error: priceError } = usePriceApi({
    token,
    reservationId,
    transactionId: reservation?.transaction?.id ?? 0,
    propertySlug: reservationInput?.bookingEngineHotel?.slug ?? '',
    roomSlug: reservationInput?.bookingEngineRoom?.slug ?? '',
    planSlug: reservationInput?.bookingEnginePlan?.slug ?? '',
    checkInDate: reservationInput?.checkInDate ?? '',
    checkOutDate: reservationInput?.checkOutDate ?? '',
    roomCount: reservationInput?.roomCount ?? 0,
    numberOfAdults: reservationInput?.numberOfAdults ?? 0,
    numberOfChildren: reservationInput?.numberOfChildren ?? 0,
    numberOfInfants: reservationInput?.numberOfInfants ?? 0,
  })
  const hotelPublication = usePublication(
    i18n.language,
    reservation?.transaction?.input?.bookingEngineHotel?.publications
  )
  const reservationPath = useReservationPath()
  const [receiptId, setReceiptId] = useState<number>()
  const [issuedReceipt, setIssuedReceipt] =
    useState<
      Pick<
        ReservationReceiptResponse,
        'id' | 'displayCount' | 'maxDisplayCount'
      >
    >()
  const [isReceiptReused, setIsReceiptReused] = useState(false)
  // Bumped when the reservation or token changes so that a late issue response
  // for the previous reservation cannot leak into the current one.
  const receiptGenerationRef = useRef(0)
  const { trigger, error: issueError } = useIssueReceiptApi(
    reservationId ?? '',
    token
  )
  const {
    trigger: downloadReceipt,
    error: downloadError,
    isLoading: isDownloading,
    reset: resetDownload,
  } = useDownloadReceiptApi(reservationId ?? '', token)
  const onIssue = useCallback(
    async (addressee: string) => {
      if (!isReceiptAvailableJst(reservation?.transaction?.input?.checkOutDate))
        return
      setReceiptId(undefined)
      setIsReceiptReused(false)
      resetDownload()
      const generation = receiptGenerationRef.current
      const issued = await trigger({ addressee })
      if (generation !== receiptGenerationRef.current) return
      const issuedId = issued?.receipts[0]
      if (!issuedId) throw new Error('No receipt was returned')
      // The backend returns the existing receipt instead of a new one when the
      // addressee is unchanged, so nothing new would appear without a notice.
      setIsReceiptReused(
        issuedReceipt?.id === issuedId ||
          Boolean(reservation?.receipts?.some(({ id }) => id === issuedId))
      )
      setIssuedReceipt((current) =>
        current?.id === issuedId
          ? current
          : {
              id: issuedId,
              displayCount:
                current?.displayCount ??
                reservation?.receipts?.[0]?.displayCount,
              maxDisplayCount:
                current?.maxDisplayCount ??
                reservation?.receipts?.[0]?.maxDisplayCount,
            }
      )
      // A failed refresh must not hide the successfully issued PDF.
      await mutate().catch(() => undefined)
    },
    [
      trigger,
      mutate,
      resetDownload,
      issuedReceipt?.id,
      reservation?.receipts,
      reservation?.transaction?.input?.checkOutDate,
    ]
  )

  useEffect(() => {
    receiptGenerationRef.current += 1
    setReceiptId(undefined)
    resetDownload()
    setIssuedReceipt(undefined)
    setIsReceiptReused(false)
  }, [reservationId, token, resetDownload])

  const onDownload = useCallback(
    async (id: number) => {
      if (!isReceiptAvailableJst(reservation?.transaction?.input?.checkOutDate))
        return
      setReceiptId(id)
      const pdfWindow = window.open('', '_blank')
      if (pdfWindow) pdfWindow.opener = null
      try {
        const downloaded = await downloadReceipt(id)
        if (!downloaded) throw new Error('No receipt URL was returned')
        setIssuedReceipt((current) =>
          current
            ? {
                ...current,
                displayCount: downloaded.displayCount,
                maxDisplayCount: downloaded.maxDisplayCount,
              }
            : current
        )
        if (pdfWindow) pdfWindow.location.replace(downloaded.signedUrl)
        else window.location.assign(downloaded.signedUrl)
        await mutate(
          (current) =>
            current
              ? {
                  ...current,
                  receipts: current.receipts?.map((receipt) => ({
                    ...receipt,
                    displayCount: downloaded.displayCount,
                    maxDisplayCount: downloaded.maxDisplayCount,
                  })),
                }
              : current,
          { revalidate: false }
        )
      } catch {
        pdfWindow?.close()
        await mutate().catch(() => undefined)
      }
    },
    [downloadReceipt, mutate, reservation?.transaction?.input?.checkOutDate]
  )

  useEffect(() => {
    onValidateTokenExpired(error)
    onValidateTokenExpired(issueError)
    onValidateTokenExpired(downloadError)
  }, [error, issueError, downloadError, onValidateTokenExpired])

  if (isLoading) {
    return <Loader />
  }

  if (!reservation) {
    navigate(reservationPath.root)
    return null
  }

  const cancelPolicy =
    reservation?.transaction?.input?.cancelPolicy ?? undefined
  const isHotel = reservation?.transaction?.input?.bookingEngineHotel?.isHotel

  return (
    <ReservationLayout
      pageTitle={t(`${translateNamespace}.pageTitle`)}
      reservationName={name}
    >
      <MultiColumn isSpColumnReverse>
        <MainColumn>
          <ReservationDetailList {...reservation} />
          <GuestDetailList {...reservation.transaction?.input} />
          <UsageInfoDetailList {...reservation.transaction?.input} />
          {hotelPublication?.notice && (
            <NoteDetailList note={hotelPublication.notice} />
          )}
          {hotelPublication?.contact && (
            <ContactDetailList contact={hotelPublication.contact} />
          )}
          <CancelPolicyDetailList
            cancelPolicy={cancelPolicy}
            isCancelled={reservation.isCancelled}
          />
          {
            // A failed price fetch must not take the receipt away from card-paying
            // guests; the backend still refuses invoice-billed / post-paid ones.
            (priceError || offersGuestReceipt(priceData?.paymentType)) && (
              <ReceiptForm
                onIssue={onIssue}
                isReceiptAvailable={isReceiptAvailable}
                receipts={reservation.receipts}
                issuedReceipt={issuedReceipt}
                isReceiptReused={isReceiptReused}
                onDownload={onDownload}
                isDownloading={isDownloading}
                downloadError={Boolean(downloadError)}
                downloadLimitExceededReceiptId={
                  isAxiosError(downloadError) &&
                  downloadError.response?.status === 403
                    ? receiptId
                    : undefined
                }
                onRetryDownload={() => {
                  if (receiptId) onDownload(receiptId)
                }}
              />
            )
          }
        </MainColumn>
        <SideColumn isSticky>
          <ReservationPanel
            isHotel={isHotel}
            reservationId={reservationId}
            checkInDate={reservation?.transaction?.input?.checkInDate}
            checkOutDate={reservation?.transaction?.input?.checkOutDate}
            numberOfAdults={reservation?.transaction?.input?.numberOfAdults}
            numberOfChildren={reservation?.transaction?.input?.numberOfChildren}
            numberOfInfants={reservation?.transaction?.input?.numberOfInfants}
            plan={reservation?.transaction?.input?.bookingEnginePlan?.slug}
            property={reservation?.transaction?.input?.bookingEngineHotel?.slug}
            room={reservation?.transaction?.input?.bookingEngineRoom?.slug}
            roomCount={reservation?.transaction?.input?.roomCount}
            qrLink={reservation?.qrCodeListUrl}
            isCancelled={reservation?.isCancelled}
            advancedCheckedInGuestUrl={reservation?.advancedCheckedInGuestUrl}
            transactionId={reservation.transaction?.id}
          />
        </SideColumn>
      </MultiColumn>
      <div className={styles.backButton}>
        <ButtonWrapper
          position="center"
          htmlType="button"
          size="large"
          variant="primary"
          onClick={() => {
            navigate(reservationPath.root)
          }}
        >
          {t(`${translateNamespace}.backToReservations`)}
        </ButtonWrapper>
      </div>
    </ReservationLayout>
  )
}
